import React, { useCallback, useContext, useEffect, useState } from 'react'
import { useAuth } from '@praxis/component-auth'
import Cookies from 'js-cookie'
import { UserContext } from 'App/UserContext'
import { envConfigs, ReactAppEnv } from 'apiConfig'
import { useEnv } from '@praxis/component-runtime-env'
import { isPol } from './../POLAccountSwitcher'
import _ from 'lodash'
import { v4 as uuid } from 'uuid'
import axios from 'axios'
import { Layout } from '@design-systems/canvas-ui'
import { UserProfile, SparkUser } from 'types'
import { IS_IMPERSONATING_KEY } from '../impersonation/impersonationStorage'
import SessionExpiryToaster from './SessionExpiryToaster'
import {
  isCachedUserUsable,
  readCachedUser,
  saveCachedUser,
  USER_LOGIN_STATE_KEY,
} from './userLoginCache'
import { validateSso } from './validateSso'
import { authRoutes, getLoginFailedUrl } from './authConfig'

const userAdapter = (initialUserProfile: UserProfile): SparkUser => {
  let outOfOfficeAttributes = {}
  if (initialUserProfile.outOfOfficeAttributes) {
    outOfOfficeAttributes = {
      oooMessage: initialUserProfile.outOfOfficeAttributes.oooMessage,
      oooFromDate: initialUserProfile.outOfOfficeAttributes.oooFromDate
        ? new Date(initialUserProfile.outOfOfficeAttributes.oooFromDate)
            .toISOString()
            .slice(0, 10)
        : null,
      oooToDate: initialUserProfile.outOfOfficeAttributes.oooToDate
        ? new Date(initialUserProfile.outOfOfficeAttributes.oooToDate)
            .toISOString()
            .slice(0, 10)
        : null,
      userOutOfOffice: initialUserProfile.outOfOfficeAttributes.userOutOfOffice,
    }
  }

  return {
    ...initialUserProfile,
    // we want partnerId to be string type, even if we got it as number from backend
    partnerId: initialUserProfile.partnerId
      ? initialUserProfile.partnerId.toString()
      : '0',
    outOfOfficeAttributes,
  }
}

const handleGet = (
  url: string,
  userEmail: string,
  requestParams?: Record<string, string>,
  requestHeaders?: Record<string, string>,
): Promise<import('axios').AxiosResponse> => {
  const config: Record<string, unknown> = {}
  if (requestParams) {
    config.params = requestParams
  }
  if (requestHeaders) {
    config.headers = requestHeaders
  }
  _.set(config, 'headers.X-Api-Id', uuid())
  _.set(config, 'headers.X-username', userEmail)
  _.set(config, 'headers.Cache-Control', 'no-cache')
  _.set(config, 'headers.Pragma', 'no-cache')
  config.withCredentials = true
  return axios
    .get(url, config)
    .then((response) => response)
    .catch((error) => {
      const message = _.get(error, 'response.data.message')
      const code = _.get(error, 'response.status')
      const traceId = _.get(error, ['response', 'headers', 'x-api-id'])
      throw Object.assign(new Error(JSON.stringify({ message, code })), {
        code,
        traceId,
      })
    })
}

const XAuthHydrator: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [xAuthCookieSet, setXAuthCookieSet] = useState(false)
  const { isAuthenticated, session, logout } = useAuth()
  const { setSparkUser } = useContext(UserContext)
  const { environment }: { environment: ReactAppEnv } = useEnv()

  const fetchXAuthCookie = useCallback(async () => {
    const ssoIsValid = validateSso(session?.userInfo?.lanId, isPol, () => {
      localStorage.removeItem(USER_LOGIN_STATE_KEY)
      Cookies.remove('SSOSESSIONINFO')
      setSparkUser(null)
      logout()
    })
    if (!ssoIsValid) return

    if (localStorage.getItem(IS_IMPERSONATING_KEY) === 'true') {
      try {
        const storedUser = readCachedUser()
        if (storedUser?.userProfileId) {
          setSparkUser(storedUser)
          setXAuthCookieSet(true)
          return
        }
      } catch {
        // Invalid cached profile: fall through and perform a normal login.
      }
      localStorage.removeItem(IS_IMPERSONATING_KEY)
    }

    const cachedUser = readCachedUser()
    if (
      isCachedUserUsable(
        cachedUser,
        session?.accessToken,
        session?.userInfo?.lanId,
      )
    ) {
      setSparkUser(cachedUser)
      setXAuthCookieSet(true)
      return
    }

    const requestHeaders = {
      Authorization: session ? `Bearer ${session.accessToken}` : '',
    }

    const requestParameters = {
      redirectUri: `${window.location.origin}`,
      authDomain: isPol ? 'POL' : 'ENTERPRISE',
    }

    const securityBaseUrl = new URL(
      `${envConfigs[environment as ReactAppEnv].securityUrl}/`,
      window.location.origin,
    )

    try {
      const loginUrl = new URL('userProfile/login', securityBaseUrl)
      const { data: user, headers } = await handleGet(
        loginUrl.href,
        session?.userInfo?.email ?? '',
        requestParameters,
        requestHeaders,
      )
      if (!user.userProfileId) {
        window.location.href = getLoginFailedUrl(headers?.['x-api-id'])
        return
      }

      const adaptedUser = userAdapter(user)
      setSparkUser(adaptedUser)
      saveCachedUser(adaptedUser)
      setXAuthCookieSet(true)
    } catch (e: any) {
      setSparkUser(null)
      localStorage.removeItem(USER_LOGIN_STATE_KEY)
      // 403 is a valid login for a user who isn't allowed in; the rest are errors
      window.location.href =
        e.code === 403 ? authRoutes.unauthorized : getLoginFailedUrl(e.traceId)
    }
  }, [session, setSparkUser, environment, logout])

  useEffect(() => {
    if (isAuthenticated() && !xAuthCookieSet) {
      fetchXAuthCookie()
    }
  }, [isAuthenticated, xAuthCookieSet, fetchXAuthCookie])

  if (!xAuthCookieSet) {
    return (
      <Layout>
        <Layout.Body>
          <p>Loading...</p>
        </Layout.Body>
      </Layout>
    )
  }

  return (
    <>
      {children}
      <SessionExpiryToaster />
    </>
  )
}

export default XAuthHydrator
